/**
 * CanvasEditor — Content editor for the active canvas tab.
 * Uses TipTapEditor in edit mode for native Ctrl+Z/Ctrl+Shift+Z undo/redo support.
 * Renders markdown content in view mode.
 * Displays suggestion overlay when an agent suggestion is active.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { Button } from '../ui/button'
import { TipTapEditor } from '../ui/TipTapEditor'
import { DocumentBody } from '../document/DocumentBody'
import { Save, X, Check, XCircle, CheckCircle, Copy, CheckCheck, RefreshCw, Sparkles, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { CanvasTab, CanvasTabState, CanvasSuggestion } from '../../hooks/useCanvas'

interface CanvasEditorProps {
  tab: CanvasTab
  tabState: CanvasTabState
  suggestion: CanvasSuggestion | null
  isGenerating?: boolean
  onContentChange: (tab: CanvasTab, content: string) => void
  onSave: (tab: CanvasTab) => Promise<void>
  onAcceptHunk: (index: number) => void
  onRejectHunk: (index: number) => void
  onAcceptAll: () => void
  onRejectAll: () => void
  onRebuild?: (tab: CanvasTab) => Promise<void>
  onFormat?: (tab: CanvasTab) => Promise<void>
  lastTurnMeta?: { latencyMs: number; totalTokens?: number }
}

export function CanvasEditor({
  tab,
  tabState,
  suggestion,
  isGenerating = false,
  onContentChange,
  onSave,
  onAcceptHunk,
  onRejectHunk,
  onAcceptAll,
  onRejectAll,
  onRebuild,
  onFormat,
  lastTurnMeta,
}: CanvasEditorProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saveFeedback, setSaveFeedback] = useState(false)
  const [isFormatting, setIsFormatting] = useState(false)
  const [isRebuilding, setIsRebuilding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const saveFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const hasSuggestion = suggestion !== null && suggestion.tab === tab

  useEffect(() => () => {
    clearTimeout(copyTimerRef.current)
    clearTimeout(saveFeedbackTimerRef.current)
    clearTimeout(errorTimerRef.current)
  }, [])

  const showError = useCallback((msg: string) => {
    setError(msg)
    clearTimeout(errorTimerRef.current)
    errorTimerRef.current = setTimeout(() => setError(null), 4000)
  }, [])

  const stripMarkdown = (text: string): string => {
    return text
      .replace(/^#{1,6}\s+/gm, '')       // headers
      .replace(/\*\*(.+?)\*\*/g, '$1')   // bold
      .replace(/\*(.+?)\*/g, '$1')       // italic
      .replace(/`(.+?)`/g, '$1')         // inline code
      .replace(/~~(.+?)~~/g, '$1')       // strikethrough
      .replace(/^\s*[-*+]\s+/gm, '')     // unordered list markers
      .replace(/^\s*\d+\.\s+/gm, '')     // ordered list markers
      .replace(/\[(.+?)\]\(.+?\)/g, '$1') // links
      .replace(/^>\s+/gm, '')            // blockquotes
      .replace(/^---+$/gm, '')           // horizontal rules
      .replace(/\n{3,}/g, '\n\n')        // collapse multiple newlines
      .trim()
  }

  const handleCopy = useCallback(async () => {
    if (!tabState.content) return
    try {
      await navigator.clipboard.writeText(stripMarkdown(tabState.content))
      setCopied(true)
      clearTimeout(copyTimerRef.current)
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000)
    } catch (err) { console.warn('[CanvasEditor] Clipboard write failed (may be unavailable in this context):', err) }
  }, [tabState.content])

  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      await onSave(tab)
      setIsEditing(false)
    } catch (err) {
      console.error('Canvas save failed:', err)
      showError('Save failed')
    } finally {
      setIsSaving(false)
    }
  }, [tab, onSave, showError])

  const handleSaveDraft = useCallback(async () => {
    setIsSaving(true)
    try {
      await onSave(tab)
      setSaveFeedback(true)
      clearTimeout(saveFeedbackTimerRef.current)
      saveFeedbackTimerRef.current = setTimeout(() => setSaveFeedback(false), 2000)
    } catch (err) {
      console.error('Save draft failed:', err)
      showError('Save failed')
    } finally {
      setIsSaving(false)
    }
  }, [tab, onSave, showError])

  const handleRebuild = useCallback(async () => {
    if (!onRebuild) return
    const confirmed = window.confirm('Rebuild will regenerate this note using all current workspace data. Your edits will be replaced. Continue?')
    if (!confirmed) return
    setIsRebuilding(true)
    try {
      await onRebuild(tab)
    } catch (err) {
      console.error('Rebuild failed:', err)
      showError('Rebuild failed')
    } finally {
      setIsRebuilding(false)
    }
  }, [tab, onRebuild, showError])

  const handleFormat = useCallback(async () => {
    if (!onFormat) return
    setIsFormatting(true)
    try {
      await onFormat(tab)
    } catch (err) {
      console.error('Format failed:', err)
      showError('Format failed')
    } finally {
      setIsFormatting(false)
    }
  }, [tab, onFormat, showError])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
  }, [])

  // Suggestion overlay
  if (hasSuggestion) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Agent suggestion: {suggestion.resolvedCount}/{suggestion.totalCount} hunks reviewed
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onAcceptAll} className="text-green-700 border-green-300">
              <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept All
            </Button>
            <Button size="sm" variant="outline" onClick={onRejectAll} className="text-red-700 border-red-300">
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject All
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {suggestion.hunks.map((hunk, index) => (
            <div
              key={hunk.id}
              className={cn(
                'rounded-lg border p-3',
                hunk.status === 'pending' && 'border-amber-300 bg-amber-50/50 dark:bg-amber-950/20',
                hunk.status === 'accepted' && 'border-green-300 bg-green-50/50 dark:bg-green-950/20',
                hunk.status === 'rejected' && 'border-red-300 bg-red-50/50 dark:bg-red-950/20 opacity-60',
              )}
            >
              <div className="text-sm font-mono leading-relaxed">
                {hunk.contextBefore && (
                  <span className="text-muted-foreground">...{hunk.contextBefore}</span>
                )}
                {hunk.removed && (
                  <span className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 line-through px-0.5">
                    {hunk.removed}
                  </span>
                )}
                {hunk.added && (
                  <span className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-0.5">
                    {hunk.added}
                  </span>
                )}
                {hunk.contextAfter && (
                  <span className="text-muted-foreground">{hunk.contextAfter}...</span>
                )}
              </div>

              {hunk.status === 'pending' && (
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAcceptHunk(index)}
                    className="text-green-700 hover:bg-green-50"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onRejectHunk(index)}
                    className="text-red-700 hover:bg-red-50"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </div>
              )}

              {hunk.status !== 'pending' && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {hunk.status === 'accepted' ? 'Accepted' : 'Rejected'}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Edit mode
  if (isEditing) {
    return (
      <div className="flex flex-col h-full">
        {/* Save/Cancel bar */}
        <div className="flex items-center justify-end gap-2 px-3 py-1 border-b border-border bg-muted/30 flex-shrink-0">
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            <Save className="h-3.5 w-3.5 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        </div>

        {/* TipTap editor with built-in toolbar + undo/redo */}
        <div className="flex-1 overflow-hidden">
          <TipTapEditor
            content={tabState.content}
            onChange={(markdown) => onContentChange(tab, markdown)}
            placeholder="Enter clinical content..."
            editable={true}
            className="h-full rounded-none border-0 [&_.clinical-editor]:rounded-none [&_.clinical-editor]:border-0"
          />
        </div>
      </div>
    )
  }

  // View mode
  return (
    <div className="flex flex-col h-full">
      {error && (
        <div className="px-3 py-1.5 bg-destructive/10 border-b border-destructive/20 text-destructive text-xs font-medium">
          {error}
        </div>
      )}

      {tabState.content && (
        <div className="px-4 pt-2">
          <span className="text-[10px] font-medium text-ai-amber-text">AI Draft — requires physician review</span>
        </div>
      )}

      {isGenerating && !tabState.content ? (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 px-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary/60" />
          <div className="text-center space-y-1">
            <p className="text-sm font-medium">Generating {tab.toUpperCase()} note...</p>
            <p className="text-xs text-muted-foreground/70">Agents are analyzing chart data. This typically takes 15-30 seconds.</p>
          </div>
        </div>
      ) : tabState.content ? (
        <div
          className="flex-1 overflow-y-auto p-4 cursor-text"
          onClick={() => setIsEditing(true)}
        >
          <DocumentBody content={tabState.content} />
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <TipTapEditor
            content=""
            onChange={(markdown) => onContentChange(tab, markdown)}
            placeholder="Start writing or wait for Casey to generate content..."
            editable={true}
            className="h-full rounded-none border-0 [&_.clinical-editor]:rounded-none [&_.clinical-editor]:border-0"
          />
        </div>
      )}

      {/* Bottom action bar */}
      <div className="flex items-center justify-end gap-1 px-1.5 py-0.5 border-t border-border flex-shrink-0 bg-background">
        <span className="text-[10px] text-muted-foreground mr-auto truncate max-w-[180px] flex items-center gap-2">
          {tabState.lastSavedAt
            ? new Date(tabState.lastSavedAt).toLocaleTimeString()
            : 'Auto-saved'}
          {lastTurnMeta && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span>{(lastTurnMeta.latencyMs / 1000).toFixed(1)}s</span>
              {lastTurnMeta.totalTokens != null && (
                <span>{lastTurnMeta.totalTokens.toLocaleString()} tok</span>
              )}
            </>
          )}
        </span>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7"
            onClick={handleCopy}
            disabled={!tabState.content}
            title={copied ? 'Copied!' : 'Copy as plain text'}
          >
            {copied
              ? <CheckCheck className="h-3.5 w-3.5 text-green-500" />
              : <Copy className="h-3.5 w-3.5" />
            }
          </Button>
          {onRebuild && (
            <Button
              size="icon"
              variant="outline"
              className="h-7 w-7 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              onClick={handleRebuild}
              disabled={isRebuilding}
              title="Rebuild note"
            >
              {isRebuilding
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />
              }
            </Button>
          )}
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7"
            onClick={handleFormat}
            disabled={!onFormat || !tabState.content || isFormatting}
            title="Format note"
          >
            {isFormatting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5" />
            }
          </Button>
          <Button
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleSaveDraft}
            disabled={!tabState.content || isSaving}
            title="Save draft"
          >
            {saveFeedback
              ? <><CheckCheck className="h-3.5 w-3.5 mr-1" /> Saved</>
              : isSaving
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>
            }
          </Button>
        </div>
      </div>
    </div>
  )
}
